import { useMemo } from "react";
import type { AnimalItem, ShareItem } from "../../../../mocks/animals";
import type { CustomerItem } from "../../../../mocks/customers";
import { useAnimals } from "../../../../hooks/useAnimals";
import { getAnimals } from "../../../../store/animalsStore";
import {
  getDisplayKupeNo,
  getDisplaySiparisNo,
} from "../../../../lib/animalLabels";

export interface ShareWithAnimal {
  share: ShareItem;
  animal: AnimalItem;
}

interface Props {
  customer: CustomerItem;
  selected: number[];
  onToggle: (shareId: number) => void;
  onSelectAll: (ids: number[]) => void;
}

function normalizePhone(value?: string) {
  return (value ?? "").replace(/\s/g, "");
}

function normalizeText(value?: string) {
  return (value ?? "").trim().toLocaleLowerCase("tr-TR");
}

function isOwnedByCustomer(share: ShareItem, customer: CustomerItem) {
  const sharePhone = normalizePhone(share.telefon);
  const customerPhone = normalizePhone(customer.telefon);

  if (sharePhone && customerPhone && sharePhone === customerPhone) {
    return true;
  }

  return normalizeText(share.musteriAdi) === normalizeText(customer.adSoyad);
}

function getShareTotal(share: ShareItem) {
  return (
    share.tutar +
    (share.kesimUcreti ?? 0) +
    (share.parcalamaUcreti ?? 0) +
    (share.tasimaUcreti ?? 0) -
    (share.indirim ?? 0)
  );
}

export function getCustomerShares(customer: CustomerItem): ShareWithAnimal[] {
  const animals = getAnimals();
  const result: ShareWithAnimal[] = [];

  for (const animal of animals) {
    for (const share of animal.hisseler ?? []) {
      if (isOwnedByCustomer(share, customer)) {
        result.push({ share, animal });
      }
    }
  }

  return result;
}

function durumColor(durum: string) {
  if (durum === "Tam") return "text-green-700 bg-green-50";
  if (durum === "Kısmi") return "text-amber-700 bg-amber-50";
  return "text-red-700 bg-red-50";
}

function animalTypeShort(animal: AnimalItem) {
  if (animal.tur === "Büyükbaş") return "B.Baş";
  if (animal.tur === "Küçükbaş") return "K.Baş";
  return "—";
}

function animalTypeBadgeClass(animal: AnimalItem) {
  return animal.tur === "Büyükbaş"
    ? "bg-farm-earth-pale text-farm-earth"
    : "bg-farm-green-pale text-farm-green";
}

export default function ShareSelectorList({
  customer,
  selected,
  onToggle,
  onSelectAll,
}: Props) {
  const { animals } = useAnimals();

  const items = useMemo<ShareWithAnimal[]>(() => {
    const result: ShareWithAnimal[] = [];

    for (const animal of animals) {
      for (const share of animal.hisseler ?? []) {
        if (isOwnedByCustomer(share, customer)) {
          result.push({ share, animal });
        }
      }
    }

    return result;
  }, [animals, customer]);

  const pending = items.filter((item) => item.share.kalan > 0);
  const allPendingIds = pending.map((item) => item.share.id);
  const allSelected =
    allPendingIds.length > 0 &&
    allPendingIds.every((id) => selected.includes(id));

  const totalBorc = items.reduce((sum, item) => sum + getShareTotal(item.share), 0);
  const totalOdenen = items.reduce((sum, item) => sum + item.share.odenen, 0);
  const totalKalan = items.reduce((sum, item) => sum + item.share.kalan, 0);
  const selectedKalan = items
    .filter((item) => selected.includes(item.share.id))
    .reduce((sum, item) => sum + item.share.kalan, 0);

  if (items.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 opacity-40">
          <i className="ri-file-list-3-line text-3xl" />
        </div>
        <p className="text-sm">Bu müşteriye ait hisse bulunamadı</p>
      </div>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="bg-gray-50 rounded-lg px-3 py-2 text-center">
          <p className="text-xs text-gray-500">Toplam Borç</p>
          <p className="text-sm font-semibold text-gray-800">
            ₺{totalBorc.toLocaleString("tr-TR")}
          </p>
        </div>

        <div className="bg-gray-50 rounded-lg px-3 py-2 text-center">
          <p className="text-xs text-gray-500">Ödenen</p>
          <p className="text-sm font-semibold text-green-700">
            ₺{totalOdenen.toLocaleString("tr-TR")}
          </p>
        </div>

        <div className="bg-red-50 rounded-lg px-3 py-2 text-center">
          <p className="text-xs text-red-500">Kalan</p>
          <p className="text-sm font-bold text-red-700">
            ₺{totalKalan.toLocaleString("tr-TR")}
          </p>
        </div>
      </div>

      {pending.length > 1 && (
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-gray-500">{pending.length} açık hisse</span>
          <button
            onClick={() => (allSelected ? onSelectAll([]) : onSelectAll(allPendingIds))}
            className="text-xs text-orange-600 hover:text-orange-800 cursor-pointer font-medium whitespace-nowrap"
          >
            {allSelected ? "Seçimi Kaldır" : "Tümünü Seç"}
          </button>
        </div>
      )}

      <div className="space-y-2">
        {items.map(({ share, animal }) => {
          const toplamUcret = getShareTotal(share);
          const isSelected = selected.includes(share.id);
          const hasPending = share.kalan > 0;

          return (
            <div
              key={share.id}
              onClick={() => hasPending && onToggle(share.id)}
              className={`border rounded-lg px-3 py-2.5 transition-all ${
                hasPending ? "cursor-pointer" : "opacity-60 cursor-default"
              } ${
                isSelected
                  ? "border-orange-400 bg-orange-50"
                  : hasPending
                  ? "border-gray-200 bg-white hover:border-orange-200"
                  : "border-gray-100 bg-gray-50"
              }`}
            >
              <div className="flex items-start gap-2.5">
                <div
                  className={`w-4 h-4 flex-shrink-0 mt-0.5 rounded border flex items-center justify-center transition-colors ${
                    isSelected
                      ? "bg-orange-500 border-orange-500"
                      : hasPending
                      ? "border-gray-300"
                      : "border-gray-200 bg-gray-100"
                  }`}
                >
                  {isSelected && <i className="ri-check-line text-white text-xs" />}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center flex-wrap gap-1.5 mb-1">
                    <span className="text-xs font-mono font-semibold text-gray-700">
                      {getDisplayKupeNo(animal)}
                    </span>
                    <span
                      className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${animalTypeBadgeClass(animal)}`}
                    >
                      {animalTypeShort(animal)}
                    </span>
                    <span className="text-xs text-gray-400">•</span>
                    <span className="text-xs text-gray-600">{share.hisseNo}</span>
                    <span
                      className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${durumColor(
                        share.durum
                      )}`}
                    >
                      {share.durum}
                    </span>
                  </div>

                  <div className="grid grid-cols-3 gap-1.5">
                    <div>
                      <p className="text-xs text-gray-400">Borç</p>
                      <p className="text-xs font-semibold text-gray-700">
                        ₺{toplamUcret.toLocaleString("tr-TR")}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-400">Ödenen</p>
                      <p className="text-xs font-semibold text-green-700">
                        ₺{share.odenen.toLocaleString("tr-TR")}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-400">Kalan</p>
                      <p
                        className={`text-xs font-bold ${
                          share.kalan > 0 ? "text-red-600" : "text-gray-400"
                        }`}
                      >
                        {share.kalan > 0
                          ? `₺${share.kalan.toLocaleString("tr-TR")}`
                          : "—"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {selected.length > 0 && (
        <div className="mt-3 bg-orange-50 border border-orange-200 rounded-lg px-4 py-2.5 flex items-center justify-between">
          <span className="text-sm text-orange-700 font-medium">
            {selected.length} hisse seçildi
          </span>
          <span className="text-sm font-bold text-orange-800">
            Kalan: ₺{selectedKalan.toLocaleString("tr-TR")}
          </span>
        </div>
      )}
    </div>
  );
}